// Rows for finished steps, plus gaps and decisions the current step has already settled.
function blockerSettledKey(blocker, index) {
  const title = String(blocker?.title || '').trim();
  return title || `blocker:${index}`;
}

function keySet(keys) {
  const out = new Set();
  const list = keys instanceof Set ? [...keys] : (Array.isArray(keys) ? keys : []);
  for (const key of list) {
    const text = String(key || '').trim().toLowerCase();
    if (text) out.add(text);
  }
  return out;
}

function splitDecision(constraint) {
  const value = String(constraint?.value || '').trim();
  const claim = String(constraint?.claim || '').trim();
  if (value && claim) {
    const suffix = `: ${value}`;
    if (claim.toLowerCase().endsWith(suffix.toLowerCase()) && claim.length > suffix.length) {
      return {
        label: value,
        question: claim.slice(0, claim.length - suffix.length).trim(),
      };
    }
  }
  return { label: value || claim, question: '' };
}

function pushTarget(targets, seen, target) {
  if (!target?.id || seen.has(target.id)) return;
  seen.add(target.id);
  targets.push(target);
}

function stepSummary(step) {
  return String(step?.pack?.workingConclusion || step?.progressSummary || '').trim();
}

/**
 * 'board' when canvas evidence or the AI patch closed the step, 'solved' when
 * the user did (Solved / %). Steps saved before `finishedBy` existed count as
 * board only with pin-case fills to show for it; otherwise they are solved.
 */
function stepSource(step) {
  if (step?.finishedBy === 'board') return 'board';
  if (step?.finishedBy === 'user') return 'solved';
  const fills = step?.progress?.doneFills;
  return Array.isArray(fills) && fills.length ? 'board' : 'solved';
}

const KIND_ORDER = { step: 0, gap: 1, decision: 2 };

/**
 * Everything settled so far, board progress first, then what the user marked
 * Solved. Within each group: finished steps (newest first), the current step's
 * settled gaps, then decision notes still on the board.
 *
 * Board rows are backed by canvas objects (pin-case fills, decision notes) or
 * the AI patch; a board gap or decision with no live shape is marked missing.
 * A finished step and a Solved gap are never missing — they need no object.
 *
 * @returns {Array<{
 *   id: string,
 *   kind: 'step' | 'gap' | 'decision',
 *   source: 'board' | 'solved',
 *   label: string,
 *   question: string,
 *   titles: string[],
 *   shapeIds: string[],
 *   missing: boolean,
 * }>}
 */
export function buildSettledStepNotes({
  finishedSteps = [],
  blockers = [],
  doneKeys = [],
  notInterestedKeys = [],
  doneFills = [],
  constraints = [],
  resolveTarget = () => null,
} = {}) {
  const done = keySet(doneKeys);
  const hidden = keySet(notInterestedKeys);
  const fills = Array.isArray(doneFills) ? doneFills : [];
  const notes = [];

  const steps = (Array.isArray(finishedSteps) ? finishedSteps : [])
    .filter(step => step && step.status === 'finished' && String(step.title || '').trim())
    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  for (const step of steps) {
    const seen = new Set();
    const targets = [];
    const stepFills = Array.isArray(step.progress?.doneFills) ? step.progress.doneFills : [];
    for (const fill of stepFills) {
      pushTarget(targets, seen, resolveTarget({
        activityId: fill?.activityId,
        shapeId: fill?.shapeId,
      }));
    }
    notes.push({
      id: `step:${step.id || String(step.title).trim().toLowerCase()}`,
      kind: 'step',
      source: stepSource(step),
      label: String(step.title).trim(),
      question: stepSummary(step),
      titles: targets.map(target => target.title).filter(Boolean),
      shapeIds: targets.map(target => target.id),
      missing: false,
    });
  }

  (Array.isArray(blockers) ? blockers : []).forEach((blocker, index) => {
    const key = blockerSettledKey(blocker, index);
    const keyLower = key.toLowerCase();
    if (!done.has(keyLower) || hidden.has(keyLower)) return;
    const matched = fills.filter(
      fill => String(fill?.doneKey || '').trim().toLowerCase() === keyLower
    );
    const label = String(blocker?.title || '').trim() || key;
    if (!matched.length) {
      notes.push({
        id: `gap:${keyLower}`,
        kind: 'gap',
        source: 'solved',
        label,
        question: '',
        titles: [],
        shapeIds: [],
        missing: false,
      });
      return;
    }

    const seen = new Set();
    const targets = [];
    for (const fill of matched) {
      pushTarget(targets, seen, resolveTarget({
        activityId: fill?.activityId,
        shapeId: fill?.shapeId,
      }));
    }
    notes.push({
      id: `gap:${keyLower}`,
      kind: 'gap',
      source: 'board',
      label,
      question: '',
      titles: targets.map(target => target.title).filter(Boolean),
      shapeIds: targets.map(target => target.id),
      missing: targets.length === 0,
    });
  });

  const seenDecisionShapes = new Set();
  for (const constraint of Array.isArray(constraints) ? constraints : []) {
    if (!constraint || constraint.active === false) continue;
    if (constraint.kind !== 'decision') continue;
    const sourceShapeId = String(constraint.sourceShapeId || '').trim();
    if (!sourceShapeId || seenDecisionShapes.has(sourceShapeId)) continue;
    seenDecisionShapes.add(sourceShapeId);
    const { label, question } = splitDecision(constraint);
    if (!label) continue;
    const target = resolveTarget({ shapeId: sourceShapeId });
    notes.push({
      id: `decision:${constraint.id || sourceShapeId}`,
      kind: 'decision',
      source: 'board',
      label,
      question,
      titles: target?.title ? [target.title] : [],
      shapeIds: target?.id ? [target.id] : [],
      missing: !target?.id,
    });
  }

  const sourceRank = note => (note.source === 'board' ? 0 : 1);
  return notes
    .map((note, index) => ({ note, index }))
    .sort((a, b) => (
      sourceRank(a.note) - sourceRank(b.note)
      || KIND_ORDER[a.note.kind] - KIND_ORDER[b.note.kind]
      || a.index - b.index
    ))
    .map(entry => entry.note);
}

export function settledNoteDetail(note, {
  evidenceCount = (count) => `${count} pins`,
  gone = 'No longer on the board',
} = {}) {
  if (!note) return '';
  if (note.missing) return gone;
  if (note.kind === 'decision' || note.kind === 'step') return note.question || '';
  if (note.shapeIds.length > 1) return evidenceCount(note.shapeIds.length);
  if (note.titles.length === 1) return note.titles[0];
  return '';
}
